import { DateTime } from 'luxon'
import type { IUser } from './IUser.js'
import type { OrganizationOverrideInfo } from '../../Generated/OrganizationOverrideInfo.ts'

/** TODO: fold `organizationOverride` into `IUser` and delete this interface — `IUser.ts` is owned elsewhere for now. */
export interface IUserWithOverride extends IUser {
  organizationOverride?: OrganizationOverrideInfo
}

export const organizationOverrideOf = (user?: IUser | null): OrganizationOverrideInfo | undefined =>
  (user as IUserWithOverride | null | undefined)?.organizationOverride

export const isOrganizationOverrideActive = (user?: IUser | null): boolean => !!organizationOverrideOf(user)

/** Whose entitlements a licence verdict is being answered with, for the operator-facing indicators. */
export const overrideEntitlementSummary = (info: OrganizationOverrideInfo): string => {
  const target = info.targetOrganizationName ?? info.targetOrganizationId
  switch (info.entitlementSource) {
    case 'EXPLICIT_SELECTION':
      return `forced ${info.licenseOverride?.tier ?? 'unknown'}${
        info.licenseOverride?.billingMissing ? ' + billing missing' : ''
      }`
    case 'UNRESOLVED':
      return `no licences — ${target} could not be read`
    default:
      return `${target}'s own licences`
  }
}

/**
 * "for another 42 min", for the operator-facing indicators. An expired override resolves as absent
 * server-side, so a non-positive remainder here is a brief staleness window, not a real state to render.
 */
export const overrideRemainingTimeLabel = (info: OrganizationOverrideInfo): string | undefined => {
  if (!info.expiresAt) return undefined

  const totalMinutes = Math.ceil(DateTime.fromISO(info.expiresAt).diff(DateTime.now(), 'minutes').minutes)
  if (totalMinutes <= 0) return undefined
  if (totalMinutes < 60) return `for another ${totalMinutes} min`

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return minutes === 0 ? `for another ${hours}h` : `for another ${hours}h ${minutes}min`
}
